'use client'

import React from 'react'
import {
  Shield,
  ShieldCheck,
  CheckCircle,
  XCircle,
  Zap,
  ArrowDown,
  Terminal,
  MessageSquare,
  Bot,
  FileText,
  GitBranch,
  RefreshCw,
  BarChart3,
  Settings,
  Play,
  Square,
} from 'lucide-react'

/**
 * Claude Code Hooks Diagrams
 * Visual components for the Claude Code hooks blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Hook Lifecycle Diagram — PreToolUse → Execution → PostToolUse → Stop
 */
export function HookLifecycleDiagram() {
  const events = [
    {
      name: 'PreToolUse',
      description: 'Evaluate before action',
      canBlock: true,
      icon: Shield,
      color: '#F90B8A',
    },
    {
      name: 'Tool Execution',
      description: 'Claude performs the action',
      canBlock: false,
      icon: Zap,
      color: '#008dcc',
      isAction: true,
    },
    {
      name: 'PostToolUse',
      description: 'Check results, auto-format',
      canBlock: false,
      icon: CheckCircle,
      color: '#8b5cf6',
    },
    {
      name: 'Stop',
      description: 'Final quality validation',
      canBlock: false,
      icon: Square,
      color: '#10b981',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          HOOK LIFECYCLE FLOW
        </h3>
        <p className='text-sm text-muted-foreground'>
          Hooks fire at four key points in every tool use cycle
        </p>
      </div>

      {/* Main flow */}
      <div className='flex flex-col items-center gap-3'>
        {events.map((event, index) => (
          <React.Fragment key={event.name}>
            <div
              className={`w-full max-w-sm p-4 rounded-xl border border-border/60 ${
                event.isAction
                  ? 'bg-[#008dcc]/10 border-[#008dcc]/30'
                  : 'bg-background'
              }`}
            >
              <div className='flex items-center gap-3'>
                <div
                  className='w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0'
                  style={{
                    backgroundColor: `${event.color}20`,
                  }}
                >
                  <event.icon
                    className='w-5 h-5'
                    style={{ color: event.color }}
                  />
                </div>
                <div className='flex-1'>
                  <div className='flex items-center gap-2'>
                    <span className='text-sm font-semibold text-foreground'>
                      {event.name}
                    </span>
                    {event.canBlock && (
                      <span className='text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#F90B8A]/20 text-[#F90B8A]'>
                        CAN BLOCK
                      </span>
                    )}
                  </div>
                  <p className='text-[10px] text-muted-foreground'>
                    {event.description}
                  </p>
                </div>
              </div>

              {/* Approve/Deny branch for PreToolUse */}
              {event.canBlock && (
                <div className='mt-3 flex items-center justify-center gap-4'>
                  <div className='flex items-center gap-1 px-3 py-1 rounded-full bg-[#10b981]/10 border border-[#10b981]/30'>
                    <CheckCircle className='w-3 h-3 text-green-600 dark:text-green-400' />
                    <span className='text-[10px] font-bold text-green-600 dark:text-green-400'>
                      APPROVE
                    </span>
                  </div>
                  <span className='text-[10px] text-muted-foreground'>or</span>
                  <div className='flex items-center gap-1 px-3 py-1 rounded-full bg-[#ef4444]/10 border border-[#ef4444]/30'>
                    <XCircle className='w-3 h-3 text-[#ef4444]' />
                    <span className='text-[10px] font-bold text-[#ef4444]'>
                      DENY
                    </span>
                  </div>
                </div>
              )}
            </div>
            {index < events.length - 1 && (
              <ArrowDown className='w-4 h-4 text-muted-foreground' />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className='mt-6 px-4 py-3 rounded-xl bg-[#F90B8A]/10 border border-[#F90B8A]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#F90B8A]'>Key point:</span>{' '}
          PreToolUse is the only hook that can block actions. Use it for
          security gates, file protection, and mandatory review enforcement.
        </p>
      </div>
    </div>
  )
}

/**
 * Three Handler Types Diagram — Command, Prompt, Agent
 */
export function ThreeHandlerTypesDiagram() {
  const handlers = [
    {
      type: 'Command',
      description: 'Run shell commands, check exit codes',
      howItWorks: 'Receives JSON on stdin, returns pass/fail via exit code',
      bestFor: 'Formatting, linting, file checks',
      complexity: 'Simple',
      icon: Terminal,
      color: '#008dcc',
      examples: ['prettier --write', 'eslint --fix', 'tsc --noEmit'],
    },
    {
      type: 'Prompt',
      description: 'LLM evaluates a prompt with context',
      howItWorks: 'Sends prompt + $ARGUMENTS to model for single-turn check',
      bestFor: 'Security classification, pattern review',
      complexity: 'Moderate',
      icon: MessageSquare,
      color: '#FDC304',
      examples: ['Security review', 'Architecture check', 'Style review'],
    },
    {
      type: 'Agent',
      description: 'Subagent with tool access verifies conditions',
      howItWorks: 'Spawns agent with Read, Grep, Glob to analyze codebase',
      bestFor: 'Cross-file verification, deep analysis',
      complexity: 'Advanced',
      icon: Bot,
      color: '#8b5cf6',
      examples: ['Pattern compliance', 'Migration check', 'RLS audit'],
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THREE HANDLER TYPES
        </h3>
        <p className='text-sm text-muted-foreground'>
          Each handler type suits different verification needs
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {handlers.map(h => (
          <div
            key={h.type}
            className='p-4 rounded-xl bg-background border-t-4 border border-border/60'
            style={{ borderTopColor: h.color }}
          >
            <div className='flex items-center gap-2 mb-3'>
              <div
                className='w-8 h-8 rounded-full flex items-center justify-center'
                style={{ backgroundColor: `${h.color}20` }}
              >
                <h.icon className='w-4 h-4' style={{ color: h.color }} />
              </div>
              <div>
                <p className='font-bold text-sm text-foreground'>{h.type}</p>
                <p
                  className='text-[9px] font-semibold'
                  style={{ color: h.color }}
                >
                  {h.complexity}
                </p>
              </div>
            </div>

            <p className='text-[10px] text-muted-foreground mb-2'>
              {h.description}
            </p>

            <div className='p-2 rounded-lg bg-muted mb-2'>
              <p className='text-[9px] text-muted-foreground italic'>
                {h.howItWorks}
              </p>
            </div>

            <p className='text-[10px] font-semibold text-foreground mb-1'>
              Best for:
            </p>
            <p className='text-[10px] mb-2' style={{ color: h.color }}>
              {h.bestFor}
            </p>

            <div className='flex flex-wrap gap-1'>
              {h.examples.map(ex => (
                <span
                  key={ex}
                  className='text-[9px] px-2 py-0.5 rounded-full bg-muted border border-border/60 text-muted-foreground'
                >
                  {ex}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className='mt-4 px-4 py-3 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#008dcc]'>Start simple:</span>{' '}
          Begin with Command hooks for formatting, graduate to Prompt hooks for
          security, then Agent hooks for deep verification.
        </p>
      </div>
    </div>
  )
}

/**
 * Governance Loop Diagram — CLAUDE.md → Hooks → TBE → CI/CD → Metrics
 */
export function GovernanceLoopDiagram() {
  const steps = [
    {
      name: 'CLAUDE.md',
      description: 'Define coding standards and constraints',
      icon: FileText,
      color: '#008dcc',
    },
    {
      name: 'Hooks',
      description: 'Enforce standards at every tool use event',
      icon: Settings,
      color: '#FDC304',
    },
    {
      name: 'Thread-Based Engineering',
      description: 'Governance framework for human review points',
      icon: GitBranch,
      color: '#8b5cf6',
    },
    {
      name: 'CI/CD Pipeline',
      description: 'Same hooks run in merge pipeline',
      icon: Play,
      color: '#F90B8A',
    },
    {
      name: 'Metrics',
      description: 'Measure churn, failure rate, security findings',
      icon: BarChart3,
      color: '#10b981',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE GOVERNANCE LOOP
        </h3>
        <p className='text-sm text-muted-foreground'>
          Hooks connect standards to enforcement to measurement
        </p>
      </div>

      {/* Circular flow */}
      <div className='flex flex-col items-center gap-3'>
        {steps.map((s, index) => (
          <React.Fragment key={s.name}>
            <div
              className='w-full max-w-sm p-3 rounded-xl bg-background border-l-4 border border-border/60'
              style={{ borderLeftColor: s.color }}
            >
              <div className='flex items-center gap-3'>
                <div
                  className='w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0'
                  style={{ backgroundColor: `${s.color}20` }}
                >
                  <s.icon className='w-4 h-4' style={{ color: s.color }} />
                </div>
                <div>
                  <span className='text-sm font-semibold text-foreground'>
                    {s.name}
                  </span>
                  <p className='text-[10px] text-muted-foreground'>
                    {s.description}
                  </p>
                </div>
              </div>
            </div>
            {index < steps.length - 1 && (
              <ArrowDown className='w-4 h-4 text-muted-foreground' />
            )}
          </React.Fragment>
        ))}

        {/* Loop back arrow */}
        <div className='flex items-center gap-2 px-4 py-2 rounded-full bg-muted border border-border/60'>
          <RefreshCw className='w-3 h-3 text-[#10b981]' />
          <span className='text-[10px] font-semibold text-muted-foreground'>
            Metrics feed back into CLAUDE.md optimization
          </span>
        </div>
      </div>

      {/* Before/After comparison */}
      <div className='grid grid-cols-2 gap-3 mt-6'>
        <div className='p-3 rounded-xl bg-[#ef4444]/10 border border-[#ef4444]/30 text-center'>
          <div className='flex items-center justify-center gap-1 mb-1'>
            <XCircle className='w-3 h-3 text-[#ef4444]' />
            <span className='text-[10px] font-bold text-[#ef4444]'>
              Without Hooks
            </span>
          </div>
          <p className='text-[10px] text-muted-foreground'>
            CLAUDE.md is advisory. Standards are suggestions, not gates.
          </p>
        </div>
        <div className='p-3 rounded-xl bg-[#10b981]/10 border border-[#10b981]/30 text-center'>
          <div className='flex items-center justify-center gap-1 mb-1'>
            <ShieldCheck className='w-3 h-3 text-green-600 dark:text-green-400' />
            <span className='text-[10px] font-bold text-green-600 dark:text-green-400'>
              With Hooks
            </span>
          </div>
          <p className='text-[10px] text-muted-foreground'>
            Every rule becomes an enforced gate that cannot be bypassed.
          </p>
        </div>
      </div>

      <div className='mt-4 px-4 py-3 rounded-xl bg-[#FDC304]/10 border border-[#FDC304]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#FDC304]'>The full loop:</span>{' '}
          Standards → Enforcement → Governance → Automation → Measurement →
          Optimization. Each component strengthens the others.
        </p>
      </div>
    </div>
  )
}
